import React, { useEffect, useRef, useState } from 'react';
import kaboom, {KaboomCtx } from 'kaboom';
import { saveScore, getTopScores, ensureAuth, ScoreEntry } from './leaderboardService';

function Game() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [gameStarted, setGameStarted] = useState(false);
  const [loadError, setLoadError] = useState<string>('');
  const gameInitialized = useRef(false);

  const kRef = useRef<KaboomCtx | null>(null);
  const musicRef = useRef<any>(null);
  const inGameSceneRef = useRef(false);
  const gamePausedRef = useRef(false);
  const pauseControlRef = useRef<{ pause: () => void; resume: () => void } | null>(null);
  const [muted, setMuted] = useState<boolean>(
    () => typeof window !== 'undefined' && localStorage.getItem('flappybox:muted') === 'true'
  );
  const mutedRef = useRef(muted);
  const [paused, setPaused] = useState(false);
  const [uiScene, setUiScene] = useState<'game' | 'gameover' | 'leaderboard'>('game');
  const [finalScore, setFinalScore] = useState(0);
  const playerNameRef = useRef('');
  const nameTextRef = useRef<any>(null);
  const restartEnabledRef = useRef(false);
  const gameClickRef = useRef<(() => void) | null>(null);
  const gameContinuousJumpRef = useRef<(() => void) | null>(null);
  const uiSceneRef = useRef<'game' | 'gameover' | 'leaderboard'>('game');
  const outerContainerRef = useRef<HTMLDivElement>(null);
  const tapButtonRef = useRef<HTMLButtonElement>(null);
  const tapRafRef = useRef<number | null>(null);

  const togglePause = () => {
    if (!inGameSceneRef.current) return;
    const willPause = !gamePausedRef.current;
    gamePausedRef.current = willPause;
    setPaused(willPause);
    if (willPause) {
      pauseControlRef.current?.pause();
    } else {
      pauseControlRef.current?.resume();
      // Re-focus canvas so keyboard input (Space) works immediately after resume.
      const canvas = canvasRef.current;
      if (canvas) setTimeout(() => canvas.focus(), 0);
    }
  };

  const controlBtnStyle: React.CSSProperties = {
    background: 'rgba(0, 0, 0, 0.6)',
    color: 'white',
    border: '2px solid rgba(255, 255, 255, 0.7)',
    borderRadius: '6px',
    padding: '6px 12px',
    fontSize: '14px',
    fontFamily: 'arial, sans-serif',
    fontWeight: 'bold',
    cursor: 'pointer',
    outline: 'none',
    minWidth: '80px',
  };

  // Constants for consistent sizing
  const GAME_WIDTH = 800;
  const GAME_HEIGHT = 600;

  // Constants for pipe dimensions and spacing
  const PIPE_HEIGHT = 320;
  const GAP_HEIGHT = 230;
  const PIPE_SPACING = 417; // actual px gap between pipe pairs at starting speed (200 px/s)
  const PIPE_WIDTH = 64;
  const MUSIC_VOLUME = 0.2;


  const buttonStyle = `
  .game-button {
    min-width: 140px;
    font-size: 24px;
    padding: 15px 30px;
    cursor: pointer;
    background-color: #E67E22;
    color: white;
    border: 3px solid #D35400;
    border-radius: 5px;
    font-family: arial, sans-serif;
    text-transform: uppercase;
    font-weight: bold;
    box-shadow: 0 4px 0 #D35400;
    transition: transform 0.1s, box-shadow 0.1s;
    position: relative;
    top: 0;
    display: inline-block;
    text-align: center;
    line-height: 1;
    margin: 0;
    outline: none;
  }

  .game-button:hover {
    transform: translateY(2px);
    box-shadow: 0 2px 0 #D35400;
  }

  .game-button:active {
    transform: translateY(4px);
    box-shadow: none;
  }

  .game-button:focus {
    outline: none;
  }

  .tap-zone-btn {
    background-color: #E67E22;
    color: white;
    border: 3px solid #D35400;
    border-radius: 8px;
    box-shadow: 0 4px 0 #D35400;
    font-family: arial, sans-serif;
    font-weight: bold;
    font-size: clamp(11px, 2.5vh, 20px);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0;
    cursor: pointer;
    outline: none;
    user-select: none;
    -webkit-user-select: none;
    touch-action: manipulation;
  }

  .tap-zone-btn:active {
    transform: translateY(2px);
    box-shadow: 0 2px 0 #D35400;
  }

  /* The canvas CSS width is always < 800px in the console layout so Kaboom's
     raw-offset mousePos never matches game units on any screen size.
     Overlay buttons must always be active to forward clicks to Kaboom. */
  .overlay-btn { pointer-events: auto; }
  `;  // Only keep the game-button style for the start screen

    // Leaderboard layout constant
    const LEADERBOARD_LAYOUT = {
      TOP_MARGIN: 25,
      TITLE_HEIGHT: 48,
      TITLE_MARGIN: 70,
      SCORE_SPACING: 40,
      MIN_SPACING: 20,
      INPUT_HEIGHT: 40,
      BACK_BUTTON_MARGIN: 20,
      BOX_HEIGHT: 400,
    };

  // Matches the canvas CSS dimensions to its container (the screen-hole wrapper)
  // so it always fills the hole exactly, even after Kaboom overwrites the style
  // with hardcoded 800px/600px on init and after every browser window resize.
  const syncCanvasToHole = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const container = canvas.parentElement;
    if (!container) return;
    canvas.style.width = container.clientWidth + 'px';
    canvas.style.height = container.clientHeight + 'px';
  };

  useEffect(() => {
    if (!gameStarted || gameInitialized.current) return;
    if (!canvasRef.current) return;

    gameInitialized.current = true;

    // Use explicit width/height without stretch/letterbox so Kaboom never reads
    // canvas.parentElement.offsetWidth — which returns 0 on Safari for a freshly
    // mounted flex child, causing shader compilation to fail with a 0x0 canvas.
    const k = kaboom({
      global: false,
      canvas: canvasRef.current,
      width: GAME_WIDTH,
      height: GAME_HEIGHT,
      scale: 1,
      debug: false,
      texFilter: "nearest",
      background: "#87CEEB",
      pixelDensity: 1,
    });
    kRef.current = k;
    k.volume(muted ? 0 : 1);

    // Kaboom sets canvas.style.width/height to 800px/600px on init.
    // Override immediately so the canvas fills the screen-hole wrapper exactly.
    syncCanvasToHole();

    canvasRef.current?.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      setLoadError('Graphics error — please refresh the page.');
    });

    k.setGravity(1600); // Set global gravity
    const loadAssets = async () => {
      try {
        await Promise.all([
          k.loadSprite('box', 'assets/box.png'),
          k.loadSprite('pipe', 'assets/pipe.png'),
          k.loadSprite('background', 'assets/background.png'),
          k.loadSprite('street', 'assets/street.png'),
          k.loadSprite('gameoverbg', 'assets/gameoverbg.png'),
          k.loadSprite('title', 'assets/flappybox.png'),
          k.loadSprite('ice', 'assets/ice.png') 
        ]);
        initializeGame(k);
      } catch (error) {
        console.error("Error loading assets:", error);
        setLoadError('Failed to load game assets');
      }
    };

    const initializeGame = (k: KaboomCtx) => {
      // Track if game has started
      let gameActive = false;

      k.loadSound("bgMusic", "assets/bgMusic.mp3"); // Load background music
      k.loadSound("deathSound", "assets/deathSound.mp3"); // Load death sound

      k.scene('game', () => {
        inGameSceneRef.current = true;
        gamePausedRef.current = false;
        setPaused(false);
        k.volume(mutedRef.current ? 0 : 1);

        // Play background music when scene loads
        // Always start at MUSIC_VOLUME; k.volume(0/1) is the global multiplier
        // for muting. Starting at 0 would make the sound permanently silent
        // because k.volume(1) later gives 0 × 1 = 0.
        const music = k.play("bgMusic", {
          loop: true,
          volume: MUSIC_VOLUME,
        });
        musicRef.current = music;
        k.onSceneLeave(() => {
          gameActive = false;
          inGameSceneRef.current = false;
          gamePausedRef.current = false;
          setPaused(false);
          pauseControlRef.current = null;
          gameClickRef.current = null;
          gameContinuousJumpRef.current = null;
          music.stop();
          musicRef.current = null;
        });

        // Reset game state at start of scene
        gameActive = false;

        // Ignore all input for the first 150 ms so the click that triggered the
        // Restart button in the gameover scene doesn't immediately fire handleJump
        // or handleContinuousJump in the new scene (causing the box to levitate).
        let inputEnabled = false;
        k.wait(0.15, () => { inputEnabled = true; });

        // Focus canvas immediately when scene starts
        const canvas = document.querySelector('canvas');
        if (canvas) {
          setTimeout(() => canvas.focus(), 0);
        }

        // Game state
        let score = 0;
        let lastPipeY: number = k.height() / 2; // Starting position for pipes

        // Add temporary instruction text
        const instructionText = k.add([
          k.text("Tap or press SPACE to start\nHold to glide", {
            size: 24,
            font: 'arial',
            lineSpacing: 8,  // Add space between lines,
            align: "center",  // Center align the text
          }),
          k.pos(k.width() / 2, k.height() / 2 - 160),
          k.anchor("center"),
          k.color(255, 255, 255),
          k.opacity(1),
          k.z(100),
        ]);

        // Set up a timer to fade out the instruction text
        let fadeStartTime = -1;
        const FADE_DURATION = 1; // 1 second fade out
        const DISPLAY_DURATION = 3; // 3 seconds total display time

        // Add an action to handle the fading
        instructionText.onUpdate(() => {
          if (gamePausedRef.current) return;
          // Start counting when game becomes active
          if (gameActive && fadeStartTime === -1) {
            fadeStartTime = k.time();
          }
          
          // If we've started the game and the display time has elapsed
          if (fadeStartTime !== -1 && k.time() > fadeStartTime + DISPLAY_DURATION - FADE_DURATION) {
            // Calculate fade based on time
            const fadeProgress = Math.min(1, (k.time() - (fadeStartTime + DISPLAY_DURATION - FADE_DURATION)) / FADE_DURATION);
            const newOpacity = Math.max(0, 1 - fadeProgress);
            
            // Apply opacity to main text
            instructionText.opacity = newOpacity;
            
            // Remove text completely after fade completes
            if (newOpacity <= 0) {
              instructionText.destroy();
            }
          }
        });

        // Ice power-up variables
        let lastIceSpawn = 0;
        const ICE_SPAWN_INTERVAL = 3; // Spawn ice every 3 pipes
        const ICE_BONUS_POINTS = 2;

        //progressive difficulty
        let gameSpeed = 200; // Starting speed
        const SPEED_INCREASE = 22;
        const MAX_SPEED = 900;
        let lastSpeedUpScore = 0;

        // Add background to fill screen exactly
        k.add([
          k.sprite('background'),
          k.pos(0, 0),
          k.scale(1),
          k.fixed(),
          k.z(10),
        ]);

        // Use your new "street" sprite for the moving floor pieces
        const floor = k.add([
          k.sprite('street'),
          k.pos(0, k.height()),
          k.anchor('botleft'),
          k.scale(1),
          k.z(51), // Should be above the background (z:10) but below the pipes
          'floor', // Add this tag
          k.area({ scale: 0.8 }), // Add this to enable collision detection
        ]);

        const floor2 = k.add([
          k.sprite('street'),
          k.pos(k.width(), k.height()),
          k.anchor('botleft'),
          k.scale(1),
          k.z(51),
          'floor', // Add this tag
          k.area({ scale: 0.8 }), // Add this to enable collision detection
        ]);

        // Update floor reset logic
        k.onUpdate(() => {
          if (!gameActive || gamePausedRef.current) return;
          const dt = k.dt();
          floor.pos.x -= gameSpeed * dt;
          floor2.pos.x -= gameSpeed * dt; if (floor.pos.x <= -k.width()) {
            floor.pos.x = floor2.pos.x + k.width();
          }
          if (floor2.pos.x <= -k.width()) {
            floor2.pos.x = floor.pos.x + k.width();
          }
        });

        // Add score display
        const shadowText = k.add([
          k.text('0', { 
            size: 35,
            font: 'arial',
          }),
          k.pos(k.width() / 2 + 2, 35 + 2), // Offset by 2px for shadow effect
          k.anchor('center'),
          k.color(0, 0, 0),
          k.z(99),
        ]);
        
        // Add main score text on top
        const scoreText = k.add([
          k.text('0', { 
            size: 35,
            font: 'arial',
          }),
          k.pos(k.width() / 2, 35),
          k.anchor('center'),
          k.color(255, 255, 255),
          k.z(100),
        ]);

        // Create player initially with body
        const player = k.add([
          k.sprite('box'),
          k.pos(k.width() * 0.3, k.height() / 2),
          k.area({
            scale: 1.0,
            offset: k.vec2(0, 0)
          }),
          k.body({
            jumpForce: 500,
            gravityScale: 0, // Start with no gravity
          }),
          k.z(55),
        ]) as any;

        // Expose freeze/unfreeze so togglePause can suspend player physics
        // without suspending the AudioContext (which k.debug.paused would do).
        let storedVelY = 0;
        pauseControlRef.current = {
          pause() {
            if (!gameActive) return;
            storedVelY = (player as any).vel.y;
            (player as any).gravityScale = 0;
            (player as any).vel.y = 0;
          },
          resume() {
            if (!gameActive) return;
            (player as any).gravityScale = 1;
            (player as any).vel.y = storedVelY;
          },
        };

        // Pause-aware pipe spawn timer. k.loop uses timer -= interval internally,
        // so it keeps accumulating wall-clock time during our custom pause and can
        // fire almost immediately after unpausing. This manual timer only advances
        // when the game is active and not paused, so it freezes on pause exactly.
        // Pre-seed so the first pipe appears ~0.5s after the player's first tap
        // instead of waiting the full 2s interval.
        let pipeTimer = (PIPE_SPACING / 200) - 0.5;
        k.onUpdate(() => {
          if (!gameActive || gamePausedRef.current) return;
          pipeTimer += k.dt();
          const spawnInterval = (PIPE_SPACING / 200) / Math.pow(gameSpeed / 200, 0.1);
          if (pipeTimer >= spawnInterval) {
            pipeTimer -= spawnInterval;
            spawnPipe();
            spawnIce();
          }
        });

        // Separate cleanup loop — destroying inside a pipe's own onUpdate causes twitching
        k.onUpdate(() => {
          if (!gameActive || gamePausedRef.current) return;
          for (const pipe of k.get('pipe')) {
            if (pipe.pos.x < -(PIPE_WIDTH + 50)) {
              pipe.destroy();
            }
          }
        });

        const handleJump = () => {
          if (!inputEnabled || gamePausedRef.current) return;
          if (!gameActive) {
            gameActive = true;
            player.gravityScale = 1;
            player.jump(500);
          } else if (player.exists()) {
            player.jump(500);
          }
        };

        const handleContinuousJump = () => {
          if (!inputEnabled || gamePausedRef.current) return;
          if (gameActive && player.exists()) {
            player.jump(300);
          }
        };

        // Add all controls in one place
        k.onKeyPress('space', handleJump);
        k.onClick(handleJump);
        k.onKeyDown('space', handleContinuousJump);
        k.onMouseDown(handleContinuousJump);
        gameClickRef.current = handleJump;
        gameContinuousJumpRef.current = handleContinuousJump;
        setUiScene('game');


        // Disable gravity initially
        (player as any).gravityScale = 0;


        // Collision detection
        player.onCollide('pipe', () => {
          if (gameActive && !gamePausedRef.current) {
            k.play("deathSound", {
              volume: 0.5,
            });
            k.go('gameover', score);
          }

        });
        player.onCollide('floor', () => {
          if (gameActive && !gamePausedRef.current) {
            k.play("deathSound", {
              volume: 0.5,
            });
            k.go('gameover', score);
          }
        });

        // Ice collection
        player.onCollide('ice', (ice: { collected: boolean; glowRef: { destroy: () => void }; destroy: () => void }) => {
          if (!ice.collected) {
            ice.collected = true;
            ice.glowRef?.destroy();
            
            // Add bonus points
            score += ICE_BONUS_POINTS;
            scoreText.text = Math.floor(score).toString();
            shadowText.text = Math.floor(score).toString();
            
            // Visual feedback
            k.add([
              k.text(`+${ICE_BONUS_POINTS}`, { size: 24, font: 'arial' }),
              k.pos(player.pos.x, player.pos.y - 40),
              k.anchor("center"),
              k.color(255, 210, 50),
              k.opacity(1),
              k.lifespan(1),
              k.move(k.UP, 100),
              k.z(100),
            ]);
            
            // Remove the ice
            ice.destroy();
          }
        });

        // Check boundaries
        k.onUpdate(() => {
          if (!gameActive || gamePausedRef.current) return;

          if (player.pos.y >= k.height() - 30 || player.pos.y < 0) {
            k.play("deathSound", { volume: 0.5 });
            k.go('gameover', score);
          }
        });

        // Update score
        k.onUpdate('pipe', (pipe) => {
          if (!gameActive || gamePausedRef.current) return;

          if (pipe.pos.x + PIPE_WIDTH < player.pos.x && !pipe.passed) {
            score += 0.5;
            pipe.passed = true;
            const scoreString = Math.floor(score).toString();
            scoreText.text = scoreString;
            shadowText.text = scoreString; // Update shadow text too
            
            // Progressive difficulty — fire once per multiple-of-3 milestone
            const milestone = Math.floor(score);
            if (milestone > lastSpeedUpScore && milestone % 3 === 0 && gameSpeed < MAX_SPEED) {
              gameSpeed += SPEED_INCREASE;
              lastSpeedUpScore = milestone;
            }
          }

        });

        function spawnPipe() {
          const MIN_GAP_Y = GAP_HEIGHT + 150;
          const MAX_GAP_Y = k.height() - GAP_HEIGHT - 150;

          // Generate new gap position with consideration of last position
          const newGapY = k.rand(
            Math.max(MIN_GAP_Y, lastPipeY - 150),
            Math.min(MAX_GAP_Y, lastPipeY + 150)
          );

          // Store this position for next spawn
          lastPipeY = newGapY;

          // Create bottom pipe
          const bottomPipe = k.add([
            k.sprite('pipe'),
            k.pos(k.width(), k.height()),
            k.anchor('bot'),
            k.area({ scale: 0.9 }), //pipe collision strictness
            'pipe',
            k.scale(1, (k.height() - (newGapY + GAP_HEIGHT / 2)) / PIPE_HEIGHT),
            k.z(50), // Ensure pipes are above the floor (z:45)
          ]);

          // Top pipe 
          // Create top pipe
          const topPipe = k.add([
            k.sprite('pipe', { flipY: true }),
            k.pos(k.width(), 0),
            k.anchor('top'),
            k.area({ scale: 0.9 }), //pipe collision strictness
            'pipe',
            k.scale(1, (newGapY - GAP_HEIGHT / 2) / PIPE_HEIGHT),
            k.z(50), // Ensure pipes are above the floor (z:45)
          ]);

          [bottomPipe, topPipe].forEach(pipe => {
            pipe.onUpdate(() => {
              if (!gameActive || gamePausedRef.current) return;
              pipe.pos.x -= gameSpeed * k.dt();
            });
          });
        }

        function spawnIce() {
          // Only spawn ice occasionally (every ICE_SPAWN_INTERVAL pipes)
          lastIceSpawn++;
          if (lastIceSpawn < ICE_SPAWN_INTERVAL) return;

          // Reset counter
          lastIceSpawn = 0;

          // Midway between consecutive pipe pairs — matches pipe spawner formula
          const currentSpawnInterval = (PIPE_SPACING / 200) / Math.pow(gameSpeed / 200, 0.1);
          const iceX = k.width() + (gameSpeed * currentSpawnInterval) / 2;
          const iceY = k.rand(100, k.height() - 100);

          // Glow halo behind the ice sprite — pulsing cyan circle
          const glow = k.add([
            k.circle(38),
            k.pos(iceX, iceY),
            k.anchor('center'),
            k.color(k.rgb(255, 210, 50)),
            k.opacity(0.5),
            k.z(59),
          ]);

          // Create the ice power-up
          const ice = k.add([
            k.sprite('ice'),
            k.pos(iceX, iceY),
            k.area(),
            k.anchor('center'),
            k.scale(0.5),
            k.z(60),
            'ice',
            { collected: false, glowRef: glow }
          ]);

          // Move ice and glow together
          ice.onUpdate(() => {
            if (!gameActive || gamePausedRef.current) return;
            if (!ice.exists() || !glow.exists()) return;
            const dt = k.dt();
            ice.pos.x -= gameSpeed * dt;
            glow.pos.x = ice.pos.x;
            glow.opacity = 0.25 + 0.15 * Math.sin(k.time() * 4);
            if (ice.pos.x < -50) {
              glow.destroy();
              ice.destroy();
            }
          });
        }        
        
      });

      // Game over scene
      k.scene('gameover', async (score: number) => {
        setUiScene('gameover');
        setFinalScore(score);
        restartEnabledRef.current = false;
        const highScore = localStorage.getItem('flappybox:highScore') || 0; //highscore
        k.shake(12);
        k.add([
          k.sprite('gameoverbg'),
          k.pos(0, 0),
          k.scale(1),
          k.fixed(),
          k.z(40),
        ]);

        // Add semi-transparent background box for score display
        k.add([
          k.rect(400, 200), // Width and height of box
          k.pos(k.width() / 2, k.height() / 3),
          k.anchor("center"),
          k.color(0, 0, 0), // Black background
          k.opacity(0.4), // 40% opacity
          k.z(55), // Below text, above background
        ]);

        // Add clean text without outlines
        k.add([
          k.text("High Score: " + Math.max(Number(highScore), score), {
            size: 32,
            font: 'arial',
          }),
          k.pos(k.width() / 2, k.height() / 3 - 50),
          k.anchor("center"),
          k.color(255, 255, 255), // Pure white text
          k.z(60),
        ]);

        k.add([
          k.text("SCORE", {
            size: 48,
            font: 'arial',
          }),
          k.pos(k.width() / 2, k.height() / 3),
          k.anchor("center"),
          k.color(255, 255, 255),
          k.z(60),
        ]);

        k.add([
          k.text(`${Math.floor(score)}`, {
            size: 64,
            font: 'arial',
          }),
          k.pos(k.width() / 2, k.height() / 3 + 50),
          k.anchor("center"),
          k.color(255, 255, 255),
          k.z(60),
        ]);

        // Update high score if current score is higher
        if (score > Number(highScore)) {
          localStorage.setItem('flappybox:highScore', score.toString());
        }

        // style tag
        if (!document.getElementById('game-styles')) {
          const styleTag = document.createElement('style');
          styleTag.id = 'game-styles';
          styleTag.textContent = buttonStyle;
          document.head.appendChild(styleTag);
        }

        // Add restart button directly in Kaboom
        const restartBtnShadow = k.add([
          k.rect(200, 60),
          k.pos(k.width() / 2 - 110, k.height() / 2 + 54), // 4px lower for shadow
          k.color(211, 84, 0), // #D35400 (darker color for shadow)
          k.anchor("center"),
          k.z(69), // Below the button
        ]);

        const restartBtn = k.add([
          k.rect(200, 60),
          k.pos(k.width() / 2 - 110, k.height() / 2 + 50),
          k.color(230, 126, 34), // #E67E22
          k.outline(3, k.rgb(211, 84, 0)), // #D35400
          k.area(),
          k.anchor("center"),
          k.z(70),
          "button"
        ]);

        // Add text on top of button
        const restartText = k.add([
          k.text("RESTART", {
            size: 30,
            font: 'arial',
          }), 
          k.pos(k.width() / 2 - 110, k.height() / 2 + 50),
          k.anchor("center"),
          k.color(255, 255, 255),
          k.z(71),
        ]);

        // Add leaderboard button with 3D effect
        const leaderboardBtnShadow = k.add([
          k.rect(200, 60),
          k.pos(k.width() / 2 + 110, k.height() / 2 + 54), // 4px lower for shadow
          k.color(211, 84, 0), // #D35400 (darker color for shadow)
          k.anchor("center"),
          k.z(69), // Below the button
        ]);

        // Add leaderboard button
        const leaderboardBtn = k.add([
          k.rect(200, 60),
          k.pos(k.width() / 2 + 110, k.height() / 2 + 50),
          k.color(230, 126, 34), // #E67E22
          k.outline(3, k.rgb(211, 84, 0)), // #D35400
          k.area(),
          k.anchor("center"),
          k.z(70),
          "button"
        ]);

        // Add text on top of button
        const leaderboardText = k.add([
          k.text("LEADERBOARD", {
            size: 25,
            font: 'arial',
          }),
          k.pos(k.width() / 2 + 110, k.height() / 2 + 50),
          k.anchor("center"), // Center the text
          k.color(255, 255, 255),
          k.z(71),
        ]);

        // Button click handlers
        restartBtn.onClick(() => {
          if (restartEnabled) {
            k.go('game');
            gameActive = false;
          }
        });

        leaderboardBtn.onClick(() => {
          k.go('leaderboard', score);
        });

        // Add hover effects
        restartBtn.onHover(() => {
          restartBtn.pos.y += 2;
          restartBtnShadow.pos.y -= 2;
          restartBtn.color = k.rgb(211, 84, 0); // #D35400 (darker)
        });

        restartBtn.onHoverEnd(() => {
          restartBtn.pos.y -= 2;
          restartBtnShadow.pos.y += 2;
          restartBtn.color = k.rgb(230, 126, 34); // #E67E22 (original)
        });

        leaderboardBtn.onHover(() => {
          leaderboardBtn.pos.y += 2;
          leaderboardBtnShadow.pos.y -= 2;
          leaderboardBtn.color = k.rgb(211, 84, 0); // #D35400 (darker)
        });

        leaderboardBtn.onHoverEnd(() => {
          leaderboardBtn.pos.y -= 2;
          leaderboardBtnShadow.pos.y += 2;
          leaderboardBtn.color = k.rgb(230, 126, 34); // #E67E22 (original)
        });


        // Add keyboard restart with delay (k.wait for x seconds)
        let restartEnabled = false;
        k.wait(1, () => {
          restartEnabled = true;
          restartEnabledRef.current = true;
        });

        // Add space key handler using existing restartEnabled flag
        k.onKeyPress('space', () => {
          if (restartEnabled) {
            k.go('game');
            gameActive = false; // Reset game state
          }
        });

      });

      k.scene('leaderboard', (score: number) => {
        setUiScene('leaderboard');
        setFinalScore(score);
        playerNameRef.current = '';
        // Add background
        k.add([
          k.sprite('gameoverbg'),
          k.pos(0, 0),
          k.scale(1),
          k.fixed(),
          k.z(40),
        ]);
      
        // Add semi-transparent background box for leaderboard
        k.add([
          k.rect(500, LEADERBOARD_LAYOUT.BOX_HEIGHT),
          k.pos(k.width() / 2, LEADERBOARD_LAYOUT.TOP_MARGIN),
          k.anchor("top"),
          k.color(0, 0, 0),
          k.opacity(0.4),
          k.z(45),
        ]);
      
        // Add leaderboard title
        k.add([
          k.text("LEADERBOARD", {
            size: LEADERBOARD_LAYOUT.TITLE_HEIGHT,
            font: 'arial',
          }),
          k.pos(k.width() / 2, LEADERBOARD_LAYOUT.TOP_MARGIN + 30),
          k.anchor("top"),
          k.color(255, 255, 255),
          k.z(50),
        ]);
      
        // Add back button directly in Kaboom
        const backBtn = k.add([
          k.rect(120, 40),
          k.pos(k.width() / 2, LEADERBOARD_LAYOUT.TOP_MARGIN + LEADERBOARD_LAYOUT.BOX_HEIGHT + 30),
          k.color(230, 126, 34), // #E67E22
          k.outline(3, k.rgb(211, 84, 0)), // #D35400
          k.area(),
          k.anchor("center"),
          k.z(70),
          "button"
        ]);
        // Add shadow for the back button
        const backBtnShadow = k.add([
          k.rect(120, 40),
          k.pos(k.width() / 2, LEADERBOARD_LAYOUT.TOP_MARGIN + LEADERBOARD_LAYOUT.BOX_HEIGHT + 104), // 4px lower for shadow
          k.color(211, 84, 0), // #D35400 (darker color for shadow)
          k.anchor("center"),
          k.z(69), // Below the button
        ]);

      
        // Add text on top of button
        const backText = k.add([
          k.text("BACK", {
            size: 20,
            font: 'arial',
          }),
          k.pos(k.width() / 2, LEADERBOARD_LAYOUT.TOP_MARGIN + LEADERBOARD_LAYOUT.BOX_HEIGHT + 30),
          k.anchor("center"),
          k.color(255, 255, 255),
          k.z(71),
        ]);
      
        // Button click handler
        backBtn.onClick(() => {
          k.go('game');
        });
      
        // Add hover effect
        backBtn.onHover(() => {
          backBtn.pos.y += 2;
          backBtnShadow.pos.y -= 2;
          backBtn.color = k.rgb(211, 84, 0); // #D35400 (darker)
        });
      
        backBtn.onHoverEnd(() => {
          backBtn.pos.y -= 2;
          backBtnShadow.pos.y += 2;
          backBtn.color = k.rgb(230, 126, 34); // #E67E22 (original)
        });

        // If no score to save, adjust back button shadow position
        if (score <= 0) {
          backBtnShadow.pos.y = LEADERBOARD_LAYOUT.TOP_MARGIN + LEADERBOARD_LAYOUT.BOX_HEIGHT + 24;
        }

      
        // Only show save score UI if there's a score to save
        if (score > 0) {
          // Create a text input simulation in Kaboom
          let playerName = "";
          const maxNameLength = 14;
          
          // Add text field background
          const inputField = k.add([
            k.rect(250, 40),
            k.pos(k.width() / 2 - 70, LEADERBOARD_LAYOUT.TOP_MARGIN + LEADERBOARD_LAYOUT.BOX_HEIGHT + 30),
            k.color(255, 255, 255),
            k.outline(2, k.rgb(0, 0, 0)),
            k.anchor("center"),
            k.area(),
            k.z(70),
          ]);
          
          // Add click handler to input field
          inputField.onClick(() => {
            // Use the browser's native prompt dialog
            const name = prompt("Enter your name:", playerName);
            if (name !== null) {
              playerName = name.substring(0, maxNameLength); // Enforce max length
              nameText.text = playerName + "_";
              nameText.color = k.rgb(0, 0, 0);
            }
          });

          // Add text display
          const nameText = k.add([
            k.text("Enter your name...", {
              size: 20,
              font: 'arial',
            }),
            k.pos(k.width() / 2 - 70, LEADERBOARD_LAYOUT.TOP_MARGIN + LEADERBOARD_LAYOUT.BOX_HEIGHT + 30),
            k.anchor("center"),
            k.color(playerName.length > 0 ? k.rgb(0, 0, 0) : k.rgb(150, 150, 150)), // Gray for placeholder
            k.z(71),
          ]);
          nameTextRef.current = nameText;
          
          // Add save button
          const saveBtn = k.add([
            k.rect(120, 40),
            k.pos(k.width() / 2 + 120, LEADERBOARD_LAYOUT.TOP_MARGIN + LEADERBOARD_LAYOUT.BOX_HEIGHT + 30),
            k.color(230, 126, 34), // #E67E22
            k.outline(3, k.rgb(211, 84, 0)), // #D35400
            k.area(),
            k.anchor("center"),
            k.z(70),
            "button"
          ]);
          // Add shadow for the save button
          const saveBtnShadow = k.add([
            k.rect(120, 40),
            k.pos(k.width() / 2 + 120, LEADERBOARD_LAYOUT.TOP_MARGIN + LEADERBOARD_LAYOUT.BOX_HEIGHT + 34), // 4px lower for shadow
            k.color(211, 84, 0), // #D35400 (darker color for shadow)
            k.anchor("center"),
            k.z(69), // Below the button
          ]);

          // Improved hover effects for save button
          saveBtn.onHover(() => {
            saveBtn.pos.y += 2;
            saveBtnShadow.pos.y -= 2;
            saveBtn.color = k.rgb(211, 84, 0); // #D35400 (darker)
          });

          saveBtn.onHoverEnd(() => {
            saveBtn.pos.y -= 2;
            saveBtnShadow.pos.y += 2;
            saveBtn.color = k.rgb(230, 126, 34); // #E67E22 (original)
          });
          
          // Add text on save button
          k.add([
            k.text("SAVE SCORE", {
              size: 18,
              font: 'arial',
            }),
            k.pos(k.width() / 2 + 120, LEADERBOARD_LAYOUT.TOP_MARGIN + LEADERBOARD_LAYOUT.BOX_HEIGHT + 30),
            k.anchor("center"),
            k.color(255, 255, 255),
            k.z(71),
          ]);
      
          // Make back button same size as save button and position it below
          backBtn.width = 120;
          backBtn.height = 40;
          backBtn.pos.y = LEADERBOARD_LAYOUT.TOP_MARGIN + LEADERBOARD_LAYOUT.BOX_HEIGHT + 100;
          backText.pos.y = LEADERBOARD_LAYOUT.TOP_MARGIN + LEADERBOARD_LAYOUT.BOX_HEIGHT + 100;
        
          // Update back button text size to match save button
          backText.textSize = 20;
          
          // Handle keyboard input for name
          k.onKeyPress(key => {
            if (key === "backspace" && playerName.length > 0) {
              playerName = playerName.slice(0, -1);
              playerNameRef.current = playerName;
              nameText.text = playerName.length > 0 ? playerName + "_" : "Enter your name...";
              nameText.color = playerName.length > 0 ? k.rgb(0, 0, 0) : k.rgb(150, 150, 150);
            } else if (key === "enter" && playerName.length > 0) {
              saveScore(playerName, score).then(() => {
                k.go('leaderboard', 0); // Refresh leaderboard
              });
            } else if (key === "space" && playerName.length < maxNameLength) {
              // Special handling for space key
              playerName += " ";
              playerNameRef.current = playerName;
              nameText.text = playerName + "_";
              nameText.color = k.rgb(0, 0, 0);
            } else if (key.length === 1 && playerName.length < maxNameLength) {
              // Only allow alphanumeric and some special characters
              if (/^[a-zA-Z0-9 _-]$/.test(key)) {
                // Check if shift key is pressed for uppercase
                const isShiftPressed = k.isKeyDown('shift');

                // Apply case transformation based on shift key
                const charToAdd = isShiftPressed ? key.toUpperCase() : key.toLowerCase();

                playerName += charToAdd;
                playerNameRef.current = playerName;
                nameText.text = playerName + "_";
                nameText.color = k.rgb(0, 0, 0); // Change to black when typing
              }
            }
          });
          
          // Save button handler
          saveBtn.onClick(() => {
            if (playerName.length > 0) {
              saveScore(playerName, score).then(() => {
                k.go('leaderboard', 0); // Refresh leaderboard
              });
            }
          });
        } else {
          // If no score to save, keep back button in original position
          backBtn.width = 120; // Add this line to make the button smaller
          backBtn.height = 40; // Add this line to make the button smaller
          backBtn.pos.y = LEADERBOARD_LAYOUT.TOP_MARGIN + LEADERBOARD_LAYOUT.BOX_HEIGHT + 50;
          backText.pos.y = LEADERBOARD_LAYOUT.TOP_MARGIN + LEADERBOARD_LAYOUT.BOX_HEIGHT + 50;
          backBtnShadow.pos.y = LEADERBOARD_LAYOUT.TOP_MARGIN + LEADERBOARD_LAYOUT.BOX_HEIGHT + 54;
          backBtnShadow.width = 120; // Add this line to match the button width
          backBtnShadow.height = 40; // Add this line to match the button height
        }
        
        // Function to fetch and display scores
        const fetchAndDisplayScores = async () => {
          try {
            // Fetch and display scores
            const scores = await getTopScores();
            scores.forEach((scoreEntry: ScoreEntry, index) => {
              // Add rank number
              k.add([
                k.text(`${index + 1}.`, { size: 24 }),
                k.pos(
                  k.width() / 2 - 200,
                  LEADERBOARD_LAYOUT.TOP_MARGIN + LEADERBOARD_LAYOUT.TITLE_HEIGHT + LEADERBOARD_LAYOUT.TITLE_MARGIN + (index * LEADERBOARD_LAYOUT.SCORE_SPACING)
                ),
                k.anchor("left"),
                k.color(255, 255, 255),
                k.z(50),
              ]);
        
              // Add player name
              k.add([
                k.text(scoreEntry.playerName || "Unknown", { size: 24 }),
                k.pos(
                  k.width() / 2 - 170,
                  LEADERBOARD_LAYOUT.TOP_MARGIN + LEADERBOARD_LAYOUT.TITLE_HEIGHT + LEADERBOARD_LAYOUT.TITLE_MARGIN + (index * LEADERBOARD_LAYOUT.SCORE_SPACING)
                ),
                k.anchor("left"),
                k.color(255, 255, 255),
                k.z(50),
              ]);
        
              // Add score
              k.add([
                k.text(scoreEntry.score.toString(), { size: 24 }),
                k.pos(
                  k.width() / 2 + 170,
                  LEADERBOARD_LAYOUT.TOP_MARGIN + LEADERBOARD_LAYOUT.TITLE_HEIGHT + LEADERBOARD_LAYOUT.TITLE_MARGIN + (index * LEADERBOARD_LAYOUT.SCORE_SPACING)
                ),
                k.anchor("right"),
                k.color(255, 255, 255),
                k.z(50),
              ]);
            });
          } catch (error) {
            console.error('Failed to fetch scores:', error);
            // Add error message to the scene
            k.add([
              k.text("Failed to load scores", { size: 24 }),
              k.pos(k.width() / 2, LEADERBOARD_LAYOUT.TOP_MARGIN + 100),
              k.anchor("center"),
              k.color(255, 0, 0),
              k.z(50),
            ]);
          }
        };
        
        // Call the function to fetch and display scores
        fetchAndDisplayScores();
      });      
      
      // Start with game scene
      k.go('game');
    };

    loadAssets();

    return () => {
      // Don't destroy Kaboom or reset gameInitialized. React StrictMode
      // double-invokes effects in dev; destroying Kaboom's WebGL context
      // here makes Safari unable to create a new one on the same canvas,
      // which fails shader compilation on remount. The instance is released
      // naturally when the page unloads.
    };
  }, [gameStarted, buttonStyle]); // Add buttonStyle here

  useEffect(() => {
    mutedRef.current = muted;
    try {
      localStorage.setItem('flappybox:muted', String(muted));
    } catch {}
    // AudioContext is never suspended with the custom pause, so k.volume() is reliable.
    kRef.current?.volume(muted ? 0 : 1);
  }, [muted]);

  useEffect(() => {
    if (!gameStarted) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Escape' && e.code !== 'KeyP') return;
      if (!inGameSceneRef.current) return;
      e.preventDefault();
      const willPause = !gamePausedRef.current;
      gamePausedRef.current = willPause;
      setPaused(willPause);
      if (willPause) {
        pauseControlRef.current?.pause();
      } else {
        pauseControlRef.current?.resume();
        const canvas = canvasRef.current;
        if (canvas) setTimeout(() => canvas.focus(), 0);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameStarted]);

      // Keep uiSceneRef in sync so native event handlers (which capture refs,
      // not state) always see the current scene without stale closures.
      useEffect(() => { uiSceneRef.current = uiScene; }, [uiScene]);

      // Native touchstart on the tap button so hold-to-glide works with the same
      // immediacy as canvas taps. Uses { passive: false } + preventDefault to kill
      // the 300ms-delayed click that would otherwise double-fire.
      // Note: canvas taps are handled by Kaboom's synthesized mouse events directly;
      // black letterbox area taps are intentionally excluded (no accidental fly).
      useEffect(() => {
        if (!gameStarted) return;
        const el = outerContainerRef.current;
        if (!el) return;
        const handleTouch = (e: TouchEvent) => {
          if (uiSceneRef.current !== 'game') return;
          const isTapButton = tapButtonRef.current != null && (e.target === tapButtonRef.current || tapButtonRef.current.contains(e.target as Node));
          if (!isTapButton) return;
          e.preventDefault();
          gameClickRef.current?.();
          // Mirror k.onMouseDown: fire handleContinuousJump every frame while held.
          let rafId: number;
          const loop = () => {
            gameContinuousJumpRef.current?.();
            rafId = requestAnimationFrame(loop);
          };
          rafId = requestAnimationFrame(loop);
          const stop = () => {
            cancelAnimationFrame(rafId);
            el.removeEventListener('touchend', stop);
            el.removeEventListener('touchcancel', stop);
          };
          el.addEventListener('touchend', stop, { once: true });
          el.addEventListener('touchcancel', stop, { once: true });
        };
        el.addEventListener('touchstart', handleTouch, { passive: false });
        return () => el.removeEventListener('touchstart', handleTouch);
      }, [gameStarted]);

      // Useeffect to start game on space key press
      useEffect(() => {
        if (!gameStarted) {
          const handleKeyPress = (e: KeyboardEvent) => {
            if (e.code === 'Space') {
              ensureAuth();
              setGameStarted(true);
            }
          };
          
          window.addEventListener('keydown', handleKeyPress);
          return () => window.removeEventListener('keydown', handleKeyPress);
        }
      }, [gameStarted]);

  // Keep the canvas CSS size synced to the screen-hole wrapper on every resize.
  // Kaboom sets canvas.style.width/height to 800px/600px on init; this effect
  // re-applies the correct container-sized dimensions whenever the viewport changes.
  useEffect(() => {
    if (!gameStarted) return;
    window.addEventListener('resize', syncCanvasToHole);
    return () => window.removeEventListener('resize', syncCanvasToHole);
  // syncCanvasToHole only reads canvasRef (stable) — no stale-closure risk.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameStarted]);

  const carbonFiberBg = (
    <svg
      width="100%" height="100%"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
    >
      <defs>
        <linearGradient id="cf-a" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#2a2f36" />
          <stop offset="0.5" stopColor="#1a1d22" />
          <stop offset="1" stopColor="#0f1115" />
        </linearGradient>
        <linearGradient id="cf-b" x1="1" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2a2f36" />
          <stop offset="0.5" stopColor="#1a1d22" />
          <stop offset="1" stopColor="#0f1115" />
        </linearGradient>
        <pattern id="cf-weave" width="24" height="24" patternUnits="userSpaceOnUse">
          <rect width="24" height="24" fill="#15171b" />
          <rect x="0" y="0" width="12" height="12" fill="url(#cf-a)" />
          <rect x="12" y="12" width="12" height="12" fill="url(#cf-a)" />
          <rect x="12" y="0" width="12" height="12" fill="url(#cf-b)" />
          <rect x="0" y="12" width="12" height="12" fill="url(#cf-b)" />
        </pattern>
        <radialGradient id="cf-vig" cx="0.5" cy="0.45" r="0.8">
          <stop offset="0" stopColor="#000" stopOpacity="0" />
          <stop offset="1" stopColor="#000" stopOpacity="0.55" />
        </radialGradient>
        <filter id="cf-noise">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="3" />
          <feColorMatrix values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.08 0" />
        </filter>
      </defs>
      <rect width="100%" height="100%" fill="url(#cf-weave)" />
      <rect width="100%" height="100%" filter="url(#cf-noise)" />
      <rect width="100%" height="100%" fill="url(#cf-vig)" />
    </svg>
  );

  const contactShadow = (
    <div style={{
      position: 'absolute',
      bottom: '2%',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '28vw',
      height: '36px',
      background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.55), rgba(0,0,0,0) 70%)',
      filter: 'blur(8px)',
      pointerEvents: 'none',
    }} />
  );

  if (loadError) {
    return <div style={{ color: 'red' }}>{loadError}</div>;
  }

  if (!gameStarted) {
    return (
      <>
        <style>{buttonStyle}</style>
        <div style={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#000',
          position: 'relative',
          overflow: 'hidden',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}>
          {carbonFiberBg}
          {contactShadow}
          {/* Console container — maintains 989:1500 aspect ratio */}
          <div style={{
            position: 'relative',
            width: 'min(100vw, calc(100vh * 0.6593))',
            aspectRatio: '989 / 1500',
            filter: 'drop-shadow(0 24px 40px rgba(0,0,0,0.55)) drop-shadow(0 8px 14px rgba(0,0,0,0.35))',
          }}>
            {/* Screen hole: logo + description */}
            <div style={{
              position: 'absolute',
              top: '21.53%',
              left: '9.20%',
              width: '81.40%',
              height: '40.53%',
              backgroundColor: '#87CEEB',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden',
            }}>
              <img
                src="assets/flappybox.png"
                alt="Flappy Box"
                style={{ maxWidth: '75%', maxHeight: '45%', objectFit: 'contain', marginBottom: '6%' }}
              />
              <div style={{
                backgroundColor: 'rgba(0,0,0,0.6)',
                borderRadius: '6px',
                padding: '4% 6%',
                maxWidth: '88%',
                textAlign: 'center',
              }}>
                <p style={{ color: 'white', fontSize: 'clamp(9px, 1.8vh, 14px)', lineHeight: 1.35, margin: 0 }}>
                  Tap or press <strong>SPACE</strong> to fly the box.<br/>
                  Navigate through buildings to score points.<br/>
                  Collect Dry Ice for bonus points!<br/>
                  How far can you go?
                </p>
              </div>
            </div>

            {/* Button hole: Start button */}
            <button
              style={{
                position: 'absolute',
                top: '71.07%',
                left: '15.87%',
                width: '67.54%',
                height: '9.93%',
                backgroundColor: '#E67E22',
                color: 'white',
                border: '3px solid #D35400',
                borderRadius: '8px',
                boxShadow: '0 4px 0 #D35400',
                fontFamily: 'arial, sans-serif',
                fontWeight: 'bold',
                fontSize: 'clamp(11px, 2.5vh, 22px)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                cursor: 'pointer',
                outline: 'none',
                zIndex: 10,
                padding: 0,
                touchAction: 'manipulation',
              }}
              onClick={() => {
                const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
                audioContext.resume();
                ensureAuth();
                setGameStarted(true);
              }}
            >
              ▶ TAP TO START
            </button>

            {/* Console PNG overlay — sits on top, screen + button holes are transparent */}
            <img
              src="assets/consolestart.png"
              alt=""
              draggable={false}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 20,
                display: 'block',
              }}
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
    <style>{buttonStyle}</style>
    <div
      ref={outerContainerRef}
      style={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#000',
        overflow: 'hidden',
        position: 'relative',
        userSelect: 'none',
        WebkitUserSelect: 'none',
      }}
    >
      {carbonFiberBg}
      {contactShadow}
      {/* Console container — maintains 989:1500 aspect ratio */}
      <div style={{
        position: 'relative',
        width: 'min(100vw, calc(100vh * 0.6593))',
        aspectRatio: '989 / 1500',
        filter: 'drop-shadow(0 24px 40px rgba(0,0,0,0.55)) drop-shadow(0 8px 14px rgba(0,0,0,0.35))',
      }}>
        {/* Screen hole: canvas + all game overlays */}
        <div style={{
          position: 'absolute',
          top: '21.53%',
          left: '9.20%',
          width: '81.40%',
          height: '40.53%',
          zIndex: 1,
        }}>
          <canvas
            ref={canvasRef}
            style={{
              width: '100%',
              height: '100%',
              display: 'block',
              touchAction: 'manipulation',
              backgroundColor: '#87CEEB',
            }} />
          {/* Invisible tap targets over Kaboom's gameover/leaderboard buttons.
              Kaboom mousePos = raw CSS offset, not scaled to game coords — misses on mobile. */}
          {(uiScene === 'gameover' || uiScene === 'leaderboard') && (
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 5 }}>
              {uiScene === 'gameover' && (
                <>
                  <button
                    className="overlay-btn"
                    style={{ position: 'absolute', left: '23.75%', top: '53.33%', width: '25%', height: '10%', opacity: 0, cursor: 'pointer', border: 'none', background: 'transparent' }}
                    onClick={() => { if (restartEnabledRef.current) kRef.current?.go('game'); }}
                    aria-label="Restart"
                  />
                  <button
                    className="overlay-btn"
                    style={{ position: 'absolute', left: '51.25%', top: '53.33%', width: '25%', height: '10%', opacity: 0, cursor: 'pointer', border: 'none', background: 'transparent' }}
                    onClick={() => kRef.current?.go('leaderboard', finalScore)}
                    aria-label="Leaderboard"
                  />
                </>
              )}
              {uiScene === 'leaderboard' && finalScore > 0 && (
                <>
                  <div
                    role="button"
                    tabIndex={0}
                    className="overlay-btn"
                    style={{ position: 'absolute', left: '25.625%', top: '72.5%', width: '31.25%', height: '6.67%', opacity: 0, cursor: 'pointer' }}
                    onClick={() => {
                      const name = prompt('Enter your name:', playerNameRef.current);
                      // prompt() suspends the AudioContext on iOS Safari — resume inside user-gesture handler.
                      const audioCtx = (kRef.current as any)?.audioCtx;
                      if (audioCtx?.state === 'suspended') audioCtx.resume();
                      if (name !== null) {
                        playerNameRef.current = name.substring(0, 14);
                        if (nameTextRef.current) {
                          nameTextRef.current.text = playerNameRef.current.length > 0 ? playerNameRef.current + '_' : 'Enter your name...';
                          nameTextRef.current.color = kRef.current?.rgb(
                            playerNameRef.current.length > 0 ? 0 : 150,
                            playerNameRef.current.length > 0 ? 0 : 150,
                            playerNameRef.current.length > 0 ? 0 : 150,
                          );
                        }
                        canvasRef.current?.focus();
                      }
                    }}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') e.currentTarget.click(); }}
                  />
                  <button
                    className="overlay-btn"
                    style={{ position: 'absolute', left: '57.5%', top: '72.5%', width: '15%', height: '6.67%', opacity: 0, cursor: 'pointer', border: 'none', background: 'transparent' }}
                    onClick={() => {
                      if (playerNameRef.current.length > 0) {
                        saveScore(playerNameRef.current, finalScore).then(() => {
                          kRef.current?.go('leaderboard', 0);
                        }).catch(() => {
                          alert('Failed to save score. Check your connection and try again.');
                        });
                      }
                    }}
                    aria-label="Save Score"
                  />
                  <button
                    className="overlay-btn"
                    style={{ position: 'absolute', left: '42.5%', top: '84.17%', width: '15%', height: '6.67%', opacity: 0, cursor: 'pointer', border: 'none', background: 'transparent' }}
                    onClick={() => {
                      const audioCtx = (kRef.current as any)?.audioCtx;
                      if (audioCtx?.state === 'suspended') audioCtx.resume();
                      kRef.current?.go('game');
                    }}
                    aria-label="Back"
                  />
                </>
              )}
              {uiScene === 'leaderboard' && finalScore <= 0 && (
                <button
                  className="overlay-btn"
                  style={{ position: 'absolute', left: '42.5%', top: '75.83%', width: '15%', height: '6.67%', opacity: 0, cursor: 'pointer', border: 'none', background: 'transparent' }}
                  onClick={() => {
                    const audioCtx = (kRef.current as any)?.audioCtx;
                    if (audioCtx?.state === 'suspended') audioCtx.resume();
                    kRef.current?.go('game');
                  }}
                  aria-label="Back"
                />
              )}
            </div>
          )}
          <div style={{ position: 'absolute', top: '2%', right: '2%', display: 'flex', gap: '3%', zIndex: 10 }}>
            <button
              onClick={() => setMuted(m => !m)}
              style={{ ...controlBtnStyle, padding: '0.3em 0.5em', fontSize: 'clamp(8px, 1.8vmin, 13px)', minWidth: 'unset' }}
              aria-label={muted ? 'Unmute' : 'Mute'}
            >
              {muted ? '🔇' : '🔊'}
            </button>
            <button
              onClick={togglePause}
              style={{ ...controlBtnStyle, padding: '0.3em 0.5em', fontSize: 'clamp(8px, 1.8vmin, 13px)', minWidth: 'unset' }}
              aria-label={paused ? 'Resume' : 'Pause'}
            >
              {paused ? '▶' : '⏸'}
            </button>
          </div>
          {paused && (
            <div style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(0,0,0,0.6)',
              color: 'white',
              zIndex: 5,
              fontFamily: 'arial, sans-serif',
              pointerEvents: 'none',
            }}>
              <div style={{ fontSize: 'clamp(16px, min(5vh, 7.4vw), 80px)', fontWeight: 'bold', letterSpacing: '4px' }}>PAUSED</div>
              <div style={{ fontSize: 'clamp(8px, min(1.5vh, 2.2vw), 22px)', marginTop: '8px', opacity: 0.85 }}>Press P or ESC to resume</div>
            </div>
          )}
        </div>

        {/* Button hole: context-aware action button — always visible as part of console */}
        <button
          ref={tapButtonRef}
          className="tap-zone-btn"
          style={{
            position: 'absolute',
            top: '71.07%',
            left: '15.87%',
            width: '67.54%',
            height: '9.93%',
            zIndex: 10,
          }}
          onMouseDown={(e) => {
            if (uiSceneRef.current !== 'game') return;
            // preventDefault stops the browser from assigning keyboard focus to this
            // button on click. Kaboom only processes key events (Space) while the
            // canvas has focus — if the button steals it, Space breaks.
            e.preventDefault();
            gameClickRef.current?.();
            const loop = () => {
              gameContinuousJumpRef.current?.();
              tapRafRef.current = requestAnimationFrame(loop);
            };
            tapRafRef.current = requestAnimationFrame(loop);
            const stopMouse = () => {
              if (tapRafRef.current !== null) { cancelAnimationFrame(tapRafRef.current); tapRafRef.current = null; }
              window.removeEventListener('mouseup', stopMouse);
            };
            window.addEventListener('mouseup', stopMouse);
          }}
          onClick={() => {
            const scene = uiSceneRef.current;
            if (scene === 'gameover') {
              if (restartEnabledRef.current) kRef.current?.go('game');
            } else if (scene === 'leaderboard') {
              const audioCtx = (kRef.current as any)?.audioCtx;
              if (audioCtx?.state === 'suspended') audioCtx.resume();
              kRef.current?.go('game');
            }
            // game scene: handled by onMouseDown — no-op here
          }}
          aria-label={uiScene === 'gameover' ? 'Restart' : uiScene === 'leaderboard' ? 'Back' : 'Tap to fly'}
        >
          {uiScene === 'game' ? '▲ TAP TO FLY · HOLD TO GLIDE' : uiScene === 'gameover' ? '↺ RESTART' : '← BACK'}
        </button>

        {/* Console PNG overlay — screen + button holes are transparent, lets canvas & button show through */}
        <img
          src="assets/console.png"
          alt=""
          draggable={false}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 20,
            display: 'block',
          }}
        />
      </div>
    </div>
    </>
  );

}

export default Game;